import { useState, useEffect, useCallback } from 'react';
import {
  Receipt, Plus, Search, Filter, Download, Eye, Send,
  CreditCard, X, Check, AlertCircle, Loader2, ChevronDown,
  Calendar, DollarSign, Tag, FileText, RefreshCw, Trash2,
  RotateCcw, Package, Key, Crown, MoreVertical, ArrowUpRight,
  Clock, Ban, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import invoiceController from '../../controllers/invoiceController';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:           { label: 'Draft',          color: '#64748b', bg: '#f1f5f9', icon: FileText },
  sent:            { label: 'Sent',           color: '#1d4ed8', bg: '#dbeafe', icon: Send },
  paid:            { label: 'Paid',           color: '#15803d', bg: '#dcfce7', icon: CheckCircle },
  partially_paid:  { label: 'Partial',        color: '#92400e', bg: '#fef3c7', icon: Clock },
  overdue:         { label: 'Overdue',        color: '#b91c1c', bg: '#fef2f2', icon: AlertTriangle },
  cancelled:       { label: 'Cancelled',      color: '#475569', bg: '#f8fafc', icon: Ban },
  refunded:        { label: 'Refunded',       color: '#6d28d9', bg: '#ede9fe', icon: RotateCcw },
};

const TYPE_CONFIG = {
  subscription: { label: 'Subscription', color: '#1d4ed8', bg: '#dbeafe', Icon: Crown },
  key:          { label: 'Key / License', color: '#0f6e56', bg: '#e1f5ee', Icon: Key },
  product:      { label: 'Product',      color: '#92400e', bg: '#fef3c7', Icon: Package },
};

const PAYMENT_METHODS = ['card', 'bank_transfer', 'upi', 'wallet', 'cash', 'cheque', 'crypto', 'other'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Spinner = ({ size = 16 }) => (
  <Loader2 size={size} style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />
);

const Badge = ({ status, type }) => {
  const cfg = type ? TYPE_CONFIG[type] : STATUS_CONFIG[status];
  if (!cfg) return null;
  const Icon = cfg.Icon || cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color }}>
      {Icon && <Icon size={10} />}
      {cfg.label}
    </span>
  );
};

const Banner = ({ type, message, onClose }) => (
  <div style={{ background: type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${type === 'error' ? '#fca5a5' : '#86efac'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, color: type === 'error' ? '#b91c1c' : '#15803d', fontSize: 13 }}>
    {type === 'error' ? <AlertCircle size={14} /> : <Check size={14} />}
    <span style={{ flex: 1 }}>{message}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={13} /></button>
  </div>
);

// ─── Payment Modal ────────────────────────────────────────────────────────────
const PaymentModal = ({ invoice, onConfirm, onClose, isSaving }) => {
  const [form, setForm] = useState({ amount: invoice?.balanceDue || 0, method: 'card', transactionId: '', notes: '' });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div style={ms.overlay}>
      <div style={ms.box}>
        <div style={ms.head}>
          <h3 style={ms.title}>Record Payment — {invoice?.invoiceNumber}</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={ms.body}>
          <div style={ms.row2}>
            <div style={ms.fg}>
              <label style={ms.lbl}>Amount (₹) *</label>
              <input style={ms.inp} type="number" min="0" value={form.amount} onChange={e => f('amount', e.target.value)} />
            </div>
            <div style={ms.fg}>
              <label style={ms.lbl}>Payment Method *</label>
              <select style={ms.inp} value={form.method} onChange={e => f('method', e.target.value)}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace('_', ' ').toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div style={ms.fg}>
            <label style={ms.lbl}>Transaction ID</label>
            <input style={ms.inp} value={form.transactionId} onChange={e => f('transactionId', e.target.value)} placeholder="Gateway transaction ID" />
          </div>
          <div style={ms.fg}>
            <label style={ms.lbl}>Notes</label>
            <textarea style={{ ...ms.inp, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional payment notes" />
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#64748b' }}>Total amount</span>
              <span style={{ fontWeight: 600 }}>{fmt(invoice?.totalAmount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#64748b' }}>Already paid</span>
              <span style={{ fontWeight: 600, color: '#15803d' }}>{fmt(invoice?.amountPaid)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: 6, fontWeight: 700 }}>
              <span>Balance due</span>
              <span style={{ color: '#b91c1c' }}>{fmt(invoice?.balanceDue)}</span>
            </div>
          </div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Cancel</button>
          <button onClick={() => onConfirm(form)} disabled={isSaving || !form.amount || !form.method}
            style={{ ...ms.primaryBtn, opacity: (isSaving || !form.amount) ? 0.6 : 1 }}>
            {isSaving ? <><Spinner size={13} /> Saving…</> : <><CreditCard size={13} style={{ display: 'inline', marginRight: 5 }} />Record Payment</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Cancel Modal ─────────────────────────────────────────────────────────────
const CancelModal = ({ invoice, onConfirm, onClose, isSaving }) => {
  const [reason, setReason] = useState('');
  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.box, maxWidth: 420 }}>
        <div style={ms.head}>
          <h3 style={ms.title}>Cancel Invoice</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={ms.body}>
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 14, lineHeight: 1.5 }}>
            Are you sure you want to cancel <strong>{invoice?.invoiceNumber}</strong>? This action cannot be undone.
          </p>
          <div style={ms.fg}>
            <label style={ms.lbl}>Reason (optional)</label>
            <textarea style={{ ...ms.inp, height: 70, resize: 'vertical' }} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for cancellation" />
          </div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Keep Invoice</button>
          <button onClick={() => onConfirm(reason)} disabled={isSaving}
            style={{ ...ms.primaryBtn, background: '#ef4444' }}>
            {isSaving ? <><Spinner size={13} /> Cancelling…</> : <><Ban size={13} style={{ display: 'inline', marginRight: 5 }} />Cancel Invoice</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Create Invoice Modal ─────────────────────────────────────────────────────
const CreateModal = ({ onSave, onClose, isSaving, userId }) => {
  const [type, setType] = useState('subscription');
  const [form, setForm] = useState({
    userId:        userId || '',
    currency:      'INR',
    subtotal:      0,
    discountAmount:0,
    taxLines:      [{ taxName: 'GST', rate: 18, amount: 0 }],
    totalAmount:   0,
    dueDate:       '',
    notes:         '',
    paymentMethod: 'upi',
    // subscription
    planId:        '',
    planName:      '',
    billingCycle:  'monthly',
    startDate:     '',
    endDate:       '',
    // key
    keyType:       'api_key',
    validFrom:     '',
    validTo:       '',
    maxUsage:      '',
    // product
    productName:   '',
    quantity:      1,
    unitPrice:     0,
  });

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Auto-calculate total
  useEffect(() => {
    const tax = form.taxLines.reduce((s, t) => s + (Number(t.amount) || 0), 0);
    f('totalAmount', Math.max(0, Number(form.subtotal) - Number(form.discountAmount) + tax));
  }, [form.subtotal, form.discountAmount, form.taxLines]);

  const handleSave = () => {
    const payload = {
      userId:        form.userId,
      currency:      form.currency,
      subtotal:      Number(form.subtotal),
      discountAmount:Number(form.discountAmount),
      taxLines:      form.taxLines.map(t => ({ ...t, rate: Number(t.rate), amount: Number(t.amount) })),
      totalAmount:   Number(form.totalAmount),
      dueDate:       form.dueDate,
      notes:         form.notes,
      paymentMethod: form.paymentMethod,
    };
    if (type === 'subscription') {
      payload.subscriptionDetails = { planId: form.planId, planName: form.planName, billingCycle: form.billingCycle, startDate: form.startDate, endDate: form.endDate };
    } else if (type === 'key') {
      payload.keyDetails = { keyType: form.keyType, validFrom: form.validFrom || undefined, validTo: form.validTo || undefined, maxUsage: form.maxUsage ? Number(form.maxUsage) : null };
    } else {
      payload.productDetails = { items: [{ name: form.productName, quantity: Number(form.quantity), unitPrice: Number(form.unitPrice), lineTotal: Number(form.quantity) * Number(form.unitPrice) }] };
    }
    onSave(type, payload);
  };

  return (
    <div style={ms.overlay}>
      <div style={{ ...ms.box, maxWidth: 620 }}>
        <div style={ms.head}>
          <h3 style={ms.title}>Create New Invoice</h3>
          <button onClick={onClose} style={ms.closeBtn}><X size={16} /></button>
        </div>
        <div style={{ ...ms.body, gap: 12 }}>
          {/* Type selector */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            {Object.entries(TYPE_CONFIG).map(([t, cfg]) => {
              const Icon = cfg.Icon;
              return (
                <button key={t} onClick={() => setType(t)}
                  style={{ flex: 1, padding: '8px 4px', border: `2px solid ${type === t ? cfg.color : '#e2e8f0'}`, borderRadius: 8, background: type === t ? cfg.bg : '#fff', color: type === t ? cfg.color : '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <Icon size={13} />{cfg.label}
                </button>
              );
            })}
          </div>

          {/* Common fields */}
          <div style={ms.row2}>
            <div style={ms.fg}>
              <label style={ms.lbl}>Due Date *</label>
              <input style={ms.inp} type="date" value={form.dueDate} onChange={e => f('dueDate', e.target.value)} />
            </div>
            <div style={ms.fg}>
              <label style={ms.lbl}>Payment Method</label>
              <select style={ms.inp} value={form.paymentMethod} onChange={e => f('paymentMethod', e.target.value)}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace('_', ' ').toUpperCase()}</option>)}
              </select>
            </div>
          </div>

          {/* Subscription fields */}
          {type === 'subscription' && (
            <>
              <div style={ms.row2}>
                <div style={ms.fg}>
                  <label style={ms.lbl}>Plan ID *</label>
                  <input style={ms.inp} value={form.planId} onChange={e => f('planId', e.target.value)} placeholder="e.g. crm, hospital" />
                </div>
                <div style={ms.fg}>
                  <label style={ms.lbl}>Plan Name *</label>
                  <input style={ms.inp} value={form.planName} onChange={e => f('planName', e.target.value)} placeholder="e.g. CRM Plan" />
                </div>
              </div>
              <div style={ms.row3}>
                <div style={ms.fg}>
                  <label style={ms.lbl}>Billing Cycle</label>
                  <select style={ms.inp} value={form.billingCycle} onChange={e => f('billingCycle', e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one_time">One Time</option>
                  </select>
                </div>
                <div style={ms.fg}>
                  <label style={ms.lbl}>Start Date</label>
                  <input style={ms.inp} type="date" value={form.startDate} onChange={e => f('startDate', e.target.value)} />
                </div>
                <div style={ms.fg}>
                  <label style={ms.lbl}>End Date</label>
                  <input style={ms.inp} type="date" value={form.endDate} onChange={e => f('endDate', e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* Key fields */}
          {type === 'key' && (
            <div style={ms.row2}>
              <div style={ms.fg}>
                <label style={ms.lbl}>Key Type *</label>
                <select style={ms.inp} value={form.keyType} onChange={e => f('keyType', e.target.value)}>
                  <option value="api_key">API Key</option>
                  <option value="license_key">License Key</option>
                  <option value="activation_key">Activation Key</option>
                  <option value="trial_key">Trial Key</option>
                </select>
              </div>
              <div style={ms.fg}>
                <label style={ms.lbl}>Max Usage (blank = unlimited)</label>
                <input style={ms.inp} type="number" min="0" value={form.maxUsage} onChange={e => f('maxUsage', e.target.value)} placeholder="e.g. 100" />
              </div>
            </div>
          )}

          {/* Product fields */}
          {type === 'product' && (
            <div style={ms.row3}>
              <div style={{ ...ms.fg, gridColumn: 'span 1' }}>
                <label style={ms.lbl}>Product Name *</label>
                <input style={ms.inp} value={form.productName} onChange={e => f('productName', e.target.value)} placeholder="Product name" />
              </div>
              <div style={ms.fg}>
                <label style={ms.lbl}>Qty</label>
                <input style={ms.inp} type="number" min="1" value={form.quantity} onChange={e => f('quantity', e.target.value)} />
              </div>
              <div style={ms.fg}>
                <label style={ms.lbl}>Unit Price (₹)</label>
                <input style={ms.inp} type="number" min="0" value={form.unitPrice} onChange={e => f('unitPrice', e.target.value)} />
              </div>
            </div>
          )}

          {/* Financials */}
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px' }}>
            <div style={ms.row3}>
              <div style={ms.fg}>
                <label style={ms.lbl}>Subtotal (₹) *</label>
                <input style={ms.inp} type="number" min="0" value={form.subtotal} onChange={e => f('subtotal', e.target.value)} />
              </div>
              <div style={ms.fg}>
                <label style={ms.lbl}>Discount (₹)</label>
                <input style={ms.inp} type="number" min="0" value={form.discountAmount} onChange={e => f('discountAmount', e.target.value)} />
              </div>
              <div style={ms.fg}>
                <label style={ms.lbl}>GST Amount (₹)</label>
                <input style={ms.inp} type="number" min="0"
                  value={form.taxLines[0]?.amount || 0}
                  onChange={e => f('taxLines', [{ taxName: 'GST', rate: 18, amount: e.target.value }])} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
              Total: {fmt(form.totalAmount)}
            </div>
          </div>

          <div style={ms.fg}>
            <label style={ms.lbl}>Notes</label>
            <textarea style={{ ...ms.inp, height: 50, resize: 'vertical' }} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes for this invoice" />
          </div>
        </div>
        <div style={ms.foot}>
          <button onClick={onClose} style={ms.ghostBtn}>Cancel</button>
          <button onClick={handleSave} disabled={isSaving || !form.dueDate || !form.subtotal}
            style={{ ...ms.primaryBtn, opacity: (isSaving || !form.dueDate || !form.subtotal) ? 0.6 : 1 }}>
            {isSaving ? <><Spinner size={13} /> Creating…</> : <><Plus size={13} style={{ display: 'inline', marginRight: 5 }} />Create Invoice</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Invoice Detail Drawer ────────────────────────────────────────────────────
const InvoiceDetail = ({ invoice, onClose, onAction, actionLoading, isSuperAdmin }) => {
  if (!invoice) return null;
  const canPay    = ['sent', 'partially_paid', 'overdue'].includes(invoice.status);
  const canSend   = invoice.status === 'draft';
  const canCancel = !['paid', 'cancelled', 'refunded'].includes(invoice.status);
  const canRefund = invoice.status === 'paid';
  const canDelete = invoice.status === 'draft' && isSuperAdmin;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 480, background: '#fff', height: '100%', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,.12)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{invoice.invoiceNumber}</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              <Badge status={invoice.status} />
              <Badge type={invoice.invoiceType} />
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={15} /></button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Financial summary */}
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
            <div style={dr.row}><span style={dr.lbl}>Subtotal</span><span>{fmt(invoice.subtotal)}</span></div>
            {invoice.discountAmount > 0 && <div style={dr.row}><span style={dr.lbl}>Discount</span><span style={{ color: '#15803d' }}>−{fmt(invoice.discountAmount)}</span></div>}
            {invoice.taxLines?.map((t, i) => (
              <div key={i} style={dr.row}><span style={dr.lbl}>{t.taxName} ({t.rate}%)</span><span>{fmt(t.amount)}</span></div>
            ))}
            <div style={{ ...dr.row, fontWeight: 700, fontSize: 15, borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 4 }}>
              <span>Total</span><span>{fmt(invoice.totalAmount)}</span>
            </div>
            <div style={dr.row}><span style={dr.lbl}>Paid</span><span style={{ color: '#15803d', fontWeight: 600 }}>{fmt(invoice.amountPaid)}</span></div>
            <div style={{ ...dr.row, fontWeight: 700, color: invoice.balanceDue > 0 ? '#b91c1c' : '#15803d' }}>
              <span>Balance due</span><span>{fmt(invoice.balanceDue)}</span>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={dr.infoCard}><span style={dr.lbl}>Issue date</span><span style={dr.val}>{fmtDate(invoice.issueDate)}</span></div>
            <div style={dr.infoCard}><span style={dr.lbl}>Due date</span><span style={{ ...dr.val, color: invoice.isOverdue ? '#b91c1c' : 'inherit' }}>{fmtDate(invoice.dueDate)}</span></div>
            {invoice.paidDate && <div style={dr.infoCard}><span style={dr.lbl}>Paid date</span><span style={dr.val}>{fmtDate(invoice.paidDate)}</span></div>}
          </div>

          {/* Type-specific details */}
          {invoice.invoiceType === 'subscription' && invoice.subscriptionDetails && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Subscription Details</div>
              <div style={dr.row}><span style={dr.lbl}>Plan</span><span>{invoice.subscriptionDetails.planName}</span></div>
              <div style={dr.row}><span style={dr.lbl}>Billing Cycle</span><span style={{ textTransform: 'capitalize' }}>{invoice.subscriptionDetails.billingCycle}</span></div>
              <div style={dr.row}><span style={dr.lbl}>Period</span><span>{fmtDate(invoice.subscriptionDetails.startDate)} → {fmtDate(invoice.subscriptionDetails.endDate)}</span></div>
              <div style={dr.row}><span style={dr.lbl}>Auto Renew</span><span>{invoice.subscriptionDetails.autoRenew ? 'Yes' : 'No'}</span></div>
            </div>
          )}

          {invoice.invoiceType === 'key' && invoice.keyDetails && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Key / License Details</div>
              <div style={dr.row}><span style={dr.lbl}>Key Type</span><span style={{ textTransform: 'capitalize' }}>{invoice.keyDetails.keyType?.replace('_', ' ')}</span></div>
              {invoice.keyDetails.licenseKey && <div style={dr.row}><span style={dr.lbl}>License Key</span><code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{invoice.keyDetails.licenseKey}</code></div>}
              <div style={dr.row}><span style={dr.lbl}>Status</span><span style={{ textTransform: 'capitalize' }}>{invoice.keyDetails.activationStatus}</span></div>
              {invoice.keyDetails.validTo && <div style={dr.row}><span style={dr.lbl}>Valid Until</span><span>{fmtDate(invoice.keyDetails.validTo)}</span></div>}
            </div>
          )}

          {invoice.invoiceType === 'product' && invoice.productDetails?.items?.length > 0 && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Product Items</div>
              {invoice.productDetails.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 13 }}>
                  <span>{item.name} <span style={{ color: '#94a3b8' }}>×{item.quantity}</span></span>
                  <span style={{ fontWeight: 600 }}>{fmt(item.lineTotal)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Payment history */}
          {invoice.payments?.length > 0 && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Payment History</div>
              {invoice.payments.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f8fafc', fontSize: 12 }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{fmt(p.amount)}</span>
                    <span style={{ color: '#94a3b8', marginLeft: 8 }}>{p.method?.replace('_', ' ').toUpperCase()}</span>
                    {p.transactionId && <span style={{ color: '#94a3b8', marginLeft: 6, fontFamily: 'monospace', fontSize: 10 }}>#{p.transactionId}</span>}
                  </div>
                  <span style={{ color: '#64748b' }}>{fmtDate(p.paidAt)}</span>
                </div>
              ))}
            </div>
          )}

          {invoice.notes && (
            <div style={dr.section}>
              <div style={dr.sectionTitle}>Notes</div>
              <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>{invoice.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {canSend && (
              <button disabled={actionLoading.send} onClick={() => onAction('send', invoice)}
                style={{ ...dr.actionBtn, background: '#1d4ed8', color: '#fff' }}>
                {actionLoading.send ? <Spinner size={13} /> : <Send size={13} />} Send Invoice
              </button>
            )}
            {canPay && (
              <button disabled={actionLoading.pay} onClick={() => onAction('pay', invoice)}
                style={{ ...dr.actionBtn, background: '#15803d', color: '#fff' }}>
                {actionLoading.pay ? <Spinner size={13} /> : <CreditCard size={13} />} Record Payment
              </button>
            )}
            {canRefund && (
              <button disabled={actionLoading.refund} onClick={() => onAction('refund', invoice)}
                style={{ ...dr.actionBtn, background: '#6d28d9', color: '#fff' }}>
                {actionLoading.refund ? <Spinner size={13} /> : <RotateCcw size={13} />} Process Refund
              </button>
            )}
            {canCancel && (
              <button disabled={actionLoading.cancel} onClick={() => onAction('cancel', invoice)}
                style={{ ...dr.actionBtn, background: '#fff', color: '#ef4444', border: '1px solid #fca5a5' }}>
                {actionLoading.cancel ? <Spinner size={13} /> : <Ban size={13} />} Cancel Invoice
              </button>
            )}
            {canDelete && (
              <button disabled={actionLoading.delete} onClick={() => onAction('delete', invoice)}
                style={{ ...dr.actionBtn, background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }}>
                {actionLoading.delete ? <Spinner size={13} /> : <Trash2 size={13} />} Delete (draft only)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Invoices = () => {
  const [invoices,      setInvoices]      = useState([]);
  const [stats,         setStats]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [pagination,    setPagination]    = useState({ page: 1, pages: 1, total: 0 });
  const [banner,        setBanner]        = useState(null);
  const [filters,       setFilters]       = useState({ page: 1, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' });
  const [search,        setSearch]        = useState('');
  const [selectedInv,   setSelectedInv]   = useState(null);
  const [modal,         setModal]         = useState(null); // 'pay' | 'cancel' | 'create' | 'refund'
  const [modalInvoice,  setModalInvoice]  = useState(null);
  const [isSaving,      setIsSaving]      = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const isSuperAdmin = invoiceController.isSuperAdmin();
  const user         = invoiceController.getCurrentUser();
  const userId       = user?.id || user?._id;

  const notify = useCallback((type, message) => {
    setBanner({ type, message });
    setTimeout(() => setBanner(null), 4000);
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const activeFilters = { ...filters };
      if (search) activeFilters.search = search;
      const [invRes, statRes] = await Promise.all([
        invoiceController.getAllInvoices(activeFilters),
        invoiceController.getStatistics(),
      ]);
      setInvoices(invRes.success ? invRes.data : []);
      setPagination(invRes.pagination || { page: 1, pages: 1, total: 0 });
      setStats(statRes.success ? statRes.data : null);
    } catch (err) {
      notify('error', 'Failed to load invoices');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, search, notify]);

  useEffect(() => { loadData(false); }, [loadData]);

  // Filter helpers
  const setFilter  = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));
  const setPage    = (p)    => setFilters(prev => ({ ...prev, page: p }));
  const setActLoading = (k, v) => setActionLoading(p => ({ ...p, [k]: v }));

  // Actions
  const handleAction = (type, invoice) => {
    setModalInvoice(invoice);
    setModal(type);
    if (selectedInv) setSelectedInv(null);
  };

  const handleSend = async (invoice) => {
    setActLoading('send', true);
    const res = await invoiceController.sendInvoice(invoice._id);
    setActLoading('send', false);
    if (res.success) { notify('success', res.message); loadData(true); }
    else notify('error', res.message);
  };

  const handleRecordPayment = async (formData) => {
    setIsSaving(true);
    const res = await invoiceController.recordPayment(modalInvoice._id, formData);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); }
    else notify('error', res.message);
  };

  const handleCancel = async (reason) => {
    setIsSaving(true);
    const res = await invoiceController.cancelInvoice(modalInvoice._id, reason);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); }
    else notify('error', res.message);
  };

  const handleCreate = async (type, data) => {
    setIsSaving(true);
    let res;
    if (type === 'subscription') res = await invoiceController.createSubscriptionInvoice(data);
    else if (type === 'key')     res = await invoiceController.createKeyInvoice(data);
    else                         res = await invoiceController.createProductInvoice(data);
    setIsSaving(false);
    if (res.success) { notify('success', res.message); setModal(null); loadData(true); }
    else notify('error', res.message);
  };

  const handleDelete = async (invoice) => {
    setActLoading('delete', true);
    const res = await invoiceController.deleteInvoice(invoice._id);
    setActLoading('delete', false);
    if (res.success) { notify('success', res.message); setSelectedInv(null); loadData(true); }
    else notify('error', res.message);
  };

  const handleDetailAction = (type, invoice) => {
    if (type === 'send')   { setSelectedInv(null); handleSend(invoice); }
    else if (type === 'delete') handleDelete(invoice);
    else handleAction(type, invoice);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={22} color="#3b82f6" />Invoices
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Manage all subscription, key, and product invoices</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => loadData(true)} disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#475569' }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
          <button onClick={() => { setModal('create'); setModalInvoice(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
            <Plus size={14} />Create Invoice
          </button>
        </div>
      </div>

      {/* Banner */}
      {banner && <Banner type={banner.type} message={banner.message} onClose={() => setBanner(null)} />}

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Revenue',    value: fmt(stats.totalRevenue),     color: '#1e293b' },
            { label: 'Collected',        value: fmt(stats.totalPaid),        color: '#15803d' },
            { label: 'Outstanding',      value: fmt(stats.totalOutstanding), color: '#b91c1c' },
            { label: 'Overdue Count',    value: stats.overdueCount,          color: '#92400e' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, fontWeight: 500 }}>{s.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadData(false)}
            placeholder="Search invoice number..."
            style={{ width: '100%', padding: '8px 8px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
        </div>
        {[
          { key: 'invoiceType', options: [['', 'All Types'], ['subscription', 'Subscription'], ['key', 'Key/License'], ['product', 'Product']] },
          { key: 'status',      options: [['', 'All Status'], ['draft', 'Draft'], ['sent', 'Sent'], ['paid', 'Paid'], ['partially_paid', 'Partial'], ['overdue', 'Overdue'], ['cancelled', 'Cancelled']] },
        ].map(({ key, options }) => (
          <select key={key} value={filters[key] || ''} onChange={e => setFilter(key, e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none' }}>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        <select value={`${filters.sortBy}_${filters.sortOrder}`}
          onChange={e => { const [s, o] = e.target.value.split('_'); setFilter('sortBy', s); setFilters(p => ({ ...p, sortOrder: o })); }}
          style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none' }}>
          <option value="createdAt_desc">Newest first</option>
          <option value="createdAt_asc">Oldest first</option>
          <option value="totalAmount_desc">Amount ↓</option>
          <option value="totalAmount_asc">Amount ↑</option>
          <option value="dueDate_asc">Due date ↑</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, gap: 10, color: '#64748b' }}>
          <Spinner size={28} /><span style={{ fontSize: 14 }}>Loading invoices…</span>
        </div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <Receipt size={40} color="#e2e8f0" style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14, margin: 0 }}>No invoices found</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  {['Invoice #', 'Type', 'User', 'Amount', 'Paid', 'Balance', 'Due Date', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background .1s', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => setSelectedInv(inv)}>
                    <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{inv.invoiceNumber}</td>
                    <td style={{ padding: '11px 14px' }}><Badge type={inv.invoiceType} /></td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 500 }}>{inv.userId?.firstName || '—'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{inv.userId?.email || ''}</div>
                    </td>
                    <td style={{ padding: '11px 14px', fontWeight: 600 }}>{fmt(inv.totalAmount)}</td>
                    <td style={{ padding: '11px 14px', color: '#15803d' }}>{fmt(inv.amountPaid)}</td>
                    <td style={{ padding: '11px 14px', color: inv.balanceDue > 0 ? '#b91c1c' : '#64748b', fontWeight: inv.balanceDue > 0 ? 600 : 400 }}>{fmt(inv.balanceDue)}</td>
                    <td style={{ padding: '11px 14px', color: inv.isOverdue ? '#b91c1c' : '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(inv.dueDate)}</td>
                    <td style={{ padding: '11px 14px' }}><Badge status={inv.status} /></td>
                    <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {inv.status === 'draft' && (
                          <button onClick={() => handleSend(inv)} title="Send"
                            style={{ background: '#dbeafe', border: 'none', borderRadius: 5, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1d4ed8' }}>
                            <Send size={12} />
                          </button>
                        )}
                        {['sent', 'partially_paid', 'overdue'].includes(inv.status) && (
                          <button onClick={() => handleAction('pay', inv)} title="Record payment"
                            style={{ background: '#dcfce7', border: 'none', borderRadius: 5, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#15803d' }}>
                            <CreditCard size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>
                Showing {((pagination.page - 1) * filters.limit) + 1}–{Math.min(pagination.page * filters.limit, pagination.total)} of {pagination.total}
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)}
                  style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: pagination.page > 1 ? 'pointer' : 'not-allowed', fontSize: 12 }}>
                  ← Prev
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const p = Math.max(1, pagination.page - 2) + i;
                  if (p > pagination.pages) return null;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: p === pagination.page ? '#1e293b' : '#fff', color: p === pagination.page ? '#fff' : '#475569', cursor: 'pointer', fontSize: 12 }}>
                      {p}
                    </button>
                  );
                })}
                <button disabled={pagination.page >= pagination.pages} onClick={() => setPage(pagination.page + 1)}
                  style={{ padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: pagination.page < pagination.pages ? 'pointer' : 'not-allowed', fontSize: 12 }}>
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail drawer */}
      {selectedInv && (
        <InvoiceDetail
          invoice={selectedInv}
          onClose={() => setSelectedInv(null)}
          onAction={handleDetailAction}
          actionLoading={actionLoading}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {/* Modals */}
      {modal === 'create' && (
        <CreateModal userId={userId} onSave={handleCreate} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
      {modal === 'pay' && modalInvoice && (
        <PaymentModal invoice={modalInvoice} onConfirm={handleRecordPayment} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
      {modal === 'cancel' && modalInvoice && (
        <CancelModal invoice={modalInvoice} onConfirm={handleCancel} onClose={() => setModal(null)} isSaving={isSaving} />
      )}
    </div>
  );
};

// ─── Style objects ─────────────────────────────────────────────────────────────
const ms = {
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010, padding: 16 },
  box:        { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.2)' },
  head:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' },
  title:      { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 },
  closeBtn:   { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' },
  body:       { padding: '16px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 },
  foot:       { padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafafa' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  row3:       { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 },
  fg:         { display: 'flex', flexDirection: 'column', gap: 4 },
  lbl:        { fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' },
  inp:        { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' },
  ghostBtn:   { padding: '8px 16px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#475569' },
  primaryBtn: { padding: '8px 18px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 },
};

const dr = {
  row:       { display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 },
  lbl:       { color: '#64748b' },
  val:       { fontWeight: 500, color: '#1e293b' },
  infoCard:  { background: '#f8fafc', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3 },
  section:   { background: '#f8fafc', borderRadius: 8, padding: '12px 14px' },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 },
  actionBtn: { width: '100%', padding: '10px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
};

export default Invoices;